import dayjs from 'dayjs';

import { isUrlValid } from 'utils/general';

const isValidEmail = (email) => {
  if (!email) return true;
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const isValidDate = (dateString) => {
  if (!dateString) return true;
  return dayjs(dateString, 'YYYY-MM-DD', true).isValid();
};

const isValidTime = (timeString) => {
  if (!timeString) return true;
  return dayjs(timeString, 'HH:mm:ss', true).isValid();
};

const isValidParty = (party) => {
  if (!Array.isArray(party) || party.length !== 2) return false;

  return party.every((row) => {
    if (!Array.isArray(row) || row.length !== 3) return false;

    const [role, firstName, lastName] = row;
    const allowedRoles = ['bride', 'groom', 'guest-of-honor', 'partner', 'bridegroom'];

    return (
      typeof role === 'string' &&
      allowedRoles.includes(role.toLowerCase()) &&
      typeof firstName === 'string' &&
      typeof lastName === 'string'
    );
  });
};

/**
 * Validates a single lead field
 * @param {string} fieldName - Field name
 * @param {any} value - Value to validate
 * @param {Object} context - { serviceTypes: [], leadSources: [] }
 * @returns {boolean}
 */
export const validateLeadField = (fieldName, value, context = {}) => {
  const { serviceTypes = [], leadSources = [] } = context;

  switch (fieldName) {
    // String
    case 'contactFirstName':
    case 'contactLastName':
    case 'contactCompanyName':
    case 'venueName':
    case 'projectName':
    case 'notes':
    case 'role':
    case 'timezone':
    case 'street':
    case 'city':
    case 'region':
    case 'state':
    case 'postcode':
    case 'postalCode':
    case 'phone':
    case 'country':
      return typeof value === 'string';

    // Email
    case 'email': {
      return typeof value === 'string' && isValidEmail(value);
    }
    // Project type
    case 'projectType': {
      return value === 'wedding' || value === 'other';
    }
    // Service type
    case 'serviceType':
    case 'serviceTypeId': {
      if (!value) return true;
      const serviceNum = Number(value);
      if (Number.isNaN(serviceNum)) return false;
      if (serviceTypes.length === 0) return true;
      return serviceTypes.some(t => t.id === serviceNum || t.value === serviceNum);
    }
    // Lead source
    case 'leadSource':
    case 'leadSourceId': {
      if (!value) return true;
      const sourceNum = Number(value);
      if (Number.isNaN(sourceNum)) return false;
      if (leadSources.length === 0) return true;
      return leadSources.some(s => s.id === sourceNum || s.value === sourceNum);
    }
    // Date
    case 'weddingDate':
    case 'eventDate':
    case 'inquiryDate':
    case 'lastContactedDate': {
      return typeof value === 'string' && isValidDate(value);
    }
    // Website
    case 'website': {
      return typeof value === 'string' && (value === '' || isUrlValid(value));
    }
    // Boolean
    case 'allDay':
    case 'allDayEvent': {
      return typeof value === 'boolean';
    }
    // Time
    case 'startTime':
    case 'endTime': {
      if (!value) return true;
      return typeof value === 'string' && isValidTime(value);
    }
    // Number
    case 'estimatedGuestCount':
    case 'estimatedBudget': {
      if (!value) return true;
      const num = Number(value);
      return !Number.isNaN(num) && num >= 0;
    }
    // Status ID
    case 'leadCustomStatusId': {
      if (!value) return true;
      return !Number.isNaN(Number(value));
    }
    // Objects
    case 'location': {
      return typeof value === 'object';
    }
    // Party
    case 'party':
      return typeof Array.isArray(value) && isValidParty(value);
    default:
      return true;
  }
};

/**
 * Validates lead update data and returns validated object or previous data
 * @param {Object} newData - New data to validate
 * @param {Object} previousData - Previous data to revert to if invalid
 * @param {Object} context - Validation context
 * @returns {{validatedData: Object, invalidFields: string[]}} - Validated data and array of invalid fields
 */
export const validateLeadUpdate = (newData, previousData, context = {}) => {
  const result = {};
  const invalidFields = [];
  /* eslint-disable-next-line no-restricted-syntax */
  for (const [key, value] of Object.entries(newData)) {
    if (validateLeadField(key, value, context)) {
      // Valid - use new value
      result[key] = value;
    } else if (previousData) {
      // Invalid - revert to previous value
      invalidFields.push(key);
      if (key in previousData) {
        result[key] = previousData[key];
      }
    }
  }

  return { validatedData: result, invalidFields };
};
